import { ChangeEvent, useEffect, useState } from 'react';
import { makeStyles, Paper, Popper } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Autocomplete, { AutocompleteRenderInputParams, createFilterOptions } from '@material-ui/lab/Autocomplete';

import iconExpandDownCapital from '../../assets/img/icon-chevron.svg';
import InputErrorIcon from '../../assets/img/input-error-icon.svg';
import Country from '../Country/Country';

import { ICountryControlOptions, ICountryControlProps } from './CountryControl.types';

import styles from './CountryControl.module.scss';

const textStyles = makeStyles({
  formControlRoot: {
    '& label.Mui-focused': {
      transform: 'translate(34px, -100%) scale(0.75)',
      color: '#A0A3B9',
    },
    '& label.MuiInputLabel-shrink': {
      transform: 'translate(34px, -100%) scale(0.75)',
      color: '#A0A3B9',
    },
  },
});

const capitalTextStyles = makeStyles({
  formControlRoot: {
    '& label.Mui-focused': {
      transform: 'translate(34px, -100%) scale(0.75)',
      color: '#A0A3B9',
    },
    '& label.MuiInputLabel-shrink': {
      transform: 'translate(34px, -100%) scale(0.75)',
      color: '#A0A3B9',
    },
    '& .MuiInputBase-root': {
      borderRadius: '12px',
      '&.Mui-focused': {
        border: '1px solid #3BB0FF',
      },
      // Add conditional error border
      '&.Mui-error': {
        border: '1px solid #F96C75', // Error border color
      },
    },
    '& .MuiAutocomplete-popupIndicator': {
      right: '12px',
      transform: 'none',
      transition: 'none',
    },
  },
});

const popperComponent = (props: any) => (
  <Popper
    {...props}
    modifiers={{
      offset: {
        enabled: true,
        offset: '0, 8', // Ensure dropdown position is directly below the input
      },
    }}
    placement="bottom-start" // Ensures dropdown starts from the left
  />
);

const CountryControl = (
  {
    id,
    name,
    placeholder,
    value,
    options,
    onSelectChange,
    errors = [],
    className,
    onCountryChange,
    asPhoneControl,
    onSubmit,
    ariaLabel,
  }: ICountryControlProps,
): JSX.Element => {
  const textClasses = capitalTextStyles();

  const [isControlDirty, setIsControlDirty] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const filterOptions: (options: ICountryControlOptions[], state: any) =>
  ICountryControlOptions[] = createFilterOptions({
    matchFrom: asPhoneControl ? 'any' : 'start',
  });

  useEffect(() => {
    if (!isControlDirty && (options.length > 0) && value) {
      let optionToEmit = options[0];

      if (value.trim() !== '') {
        const filteredOptions = options.find((option: ICountryControlOptions) => (
          option.value.toString().toLowerCase() === value.toLowerCase()));

        optionToEmit = filteredOptions ?? options[0];
      }

      setSelectedOption(optionToEmit);
    }
  }, [isControlDirty, onCountryChange, options, value]);

  const paperComponent = (children: any) => (
    <Paper
      className={styles.paperDark}
      style={{
        ...({
          backgroundColor: '#191C2E',
          borderRadius: '8px',
        }),
        width: asPhoneControl ? 300 : 'auto',
      }}
    >
      {children}
    </Paper>
  );

  const inputComponent = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={placeholder}
      className={styles.inputControl}
      InputProps={{
        ...params.InputProps,
        autoComplete: 'new-password', // disable autocomplete and autofill
        'aria-label': ariaLabel,
        itemID: id,
      }}
    />
  );

  const capitalInputComponent = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={isFocused || value ? '' : placeholder}
      className={styles.inputControlDark}
      InputLabelProps={{
        className: !selectedOption ? styles.placeholderWithMargin : '',
      }}
      InputProps={{
        ...params.InputProps,
        autoComplete: 'new-password', // disable autocomplete and autofill
        'aria-label': ariaLabel,
        itemID: id,
        disableUnderline: true,
        style: {
          paddingLeft: '34px', // Add padding to make space for the flag
        },
        onFocus: handleFocus,
        onBlur: handleBlur,
        className: errors.length > 0 ? 'Mui-error' : '', // Dynamically apply error class
      }}
    />
  );

  const optionComponent = (option: any) => (
    <Country
      name={option.label as string}
      id={option.countryId ?? option.value as string}
      phoneCode={asPhoneControl ? option.value : null}
    />
  );

  const handleOnKeyPress = (event: { key: any }) => {
    const { key } = event;
    key === 'Enter' && onSubmit?.();
  };

  return (
    <div
      className={[
        styles.wrapper,
        asPhoneControl && styles.phoneControl,
        className,
      ].join(' ')}
    >
      {selectedOption ? (
        <div className={styles.flag}>
          <Country id={selectedOption.countryId ?? selectedOption.value.toString()} />
        </div>
      ) : (
        <div className={styles.flag}>
          <Country id={'WD'} />
        </div>
      )}
      <Autocomplete
        openOnFocus
        disableClearable
        autoHighlight
        options={options}
        filterOptions={filterOptions}
        value={selectedOption ?? null}
        className={styles.control}
        popupIcon={(
          <img
            id="countryChoiceIcon"
            aria-label="country choice drop down icon"
            src={iconExpandDownCapital}
            alt="up down icon"
          />
)}
        getOptionLabel={(option) => (asPhoneControl ? option.value.toString() : option.label)}
        classes={{ root: textClasses.formControlRoot }}
        PaperComponent={({ children }) => paperComponent(children)}
        PopperComponent={popperComponent}
        renderInput={(params: AutocompleteRenderInputParams) => (
          capitalInputComponent(params)
        )}
        renderOption={(option: ICountryControlOptions) => optionComponent(option)}
        onChange={(event: ChangeEvent<any>, selectedValue) => {
          const option = selectedValue as ICountryControlOptions;

          setSelectedOption(option);
          setIsControlDirty(true);
          onSelectChange && onSelectChange(event);
          onCountryChange && onCountryChange(option.value as string);
        }}
        getOptionSelected={(option: ICountryControlOptions, currentOption: ICountryControlOptions) => (
          (currentOption.value !== '') && (option.value === currentOption.value)
        )}
        onKeyUp={handleOnKeyPress}
      />

      {errors?.length > 0 && (
      <>
        <div className={styles.errorContainer}>
          <img src={InputErrorIcon} alt="Error icon" className={styles.errorIcon} />
          <p key={errors[0].id} className={styles.capitalError}>
            {errors[0].error}
          </p>
        </div>
      </>
      )}
    </div>
  );
};

export default CountryControl;
